import { useMemo } from "react";
import type { DepositRequired } from "../../../common/jsonRpcTypes";
import type { PreorderSelections } from "../bookingForm/BookingForm";

/**
 * Calculate the preorder payment amount based on tax configuration.
 */
function calculatePreorderPaymentAmount(
  preorderSelections: PreorderSelections,
  isPreorderTaxExclusive: boolean,
): number {
  if (isPreorderTaxExclusive) {
    return preorderSelections.requiredPaymentGross ?? preorderSelections.requiredPaymentAmount;
  }
  return preorderSelections.requiredPaymentAmount;
}

/**
 * Calculate the deposit payment amount based on tax configuration.
 */
function calculateDepositPaymentAmount(depositRequired: DepositRequired, isDepositTaxExclusive: boolean): number {
  if (isDepositTaxExclusive && depositRequired.gross_amount != null) {
    return depositRequired.gross_amount;
  }
  return depositRequired.total;
}

/**
 * Calculate payment amount when takeWithPreorder is false (pay either deposit OR preorder).
 */
function calculatePaymentAmountWithoutPreorder(
  depositRequired: DepositRequired | null | undefined,
  preorderSelections: PreorderSelections | null,
  isAuthOnly: boolean,
  isDepositTaxExclusive: boolean,
  isPreorderTaxExclusive: boolean,
): number {
  // Only treat the pre-order as the amount due when it is actually payable now.
  // A pre-order with items but no required payment (pay-at-venue) must fall
  // through to the deposit — the pre-order itself isn't being paid on card.
  const hasPreorderPayment = preorderSelections && preorderSelections.requiredPaymentAmount > 0;

  if (hasPreorderPayment) {
    return calculatePreorderPaymentAmount(preorderSelections, isPreorderTaxExclusive);
  }

  if (depositRequired && !isAuthOnly) {
    return calculateDepositPaymentAmount(depositRequired, isDepositTaxExclusive);
  }

  return 0;
}

/**
 * Calculate payment amount when takeWithPreorder is true (pay both deposit and preorder).
 */
function calculatePaymentAmountWithPreorder(
  depositRequired: DepositRequired | null | undefined,
  preorderSelections: PreorderSelections | null,
  isAuthOnly: boolean,
  isDepositOnly: boolean,
  isDepositTaxExclusive: boolean,
  isPreorderTaxExclusive: boolean,
): number {
  let amount = 0;

  if (depositRequired && !isAuthOnly) {
    amount += calculateDepositPaymentAmount(depositRequired, isDepositTaxExclusive);
  }

  if (!isDepositOnly && preorderSelections) {
    amount += calculatePreorderPaymentAmount(preorderSelections, isPreorderTaxExclusive);
  }

  return amount;
}

/**
 * Calculate the total payment amount based on deposit and preorder requirements.
 * Extracted from PaymentStep to reduce cognitive complexity.
 */
export const usePaymentCalculations = (
  depositRequired: DepositRequired | null | undefined,
  preorderSelections: PreorderSelections | null,
  isAuthOnly: boolean,
  isDepositOnly: boolean,
  isDepositTaxExclusive: boolean,
  isPreorderTaxExclusive: boolean,
) => {
  return useMemo(() => {
    const takeWithPreorder = depositRequired?.takeWithPreorder ?? false;

    if (takeWithPreorder) {
      return calculatePaymentAmountWithPreorder(
        depositRequired,
        preorderSelections,
        isAuthOnly,
        isDepositOnly,
        isDepositTaxExclusive,
        isPreorderTaxExclusive,
      );
    }

    return calculatePaymentAmountWithoutPreorder(
      depositRequired,
      preorderSelections,
      isAuthOnly,
      isDepositTaxExclusive,
      isPreorderTaxExclusive,
    );
  }, [depositRequired, isAuthOnly, isDepositOnly, preorderSelections, isDepositTaxExclusive, isPreorderTaxExclusive]);
};
